import { CloseIcon } from "@chakra-ui/icons";
import {
  Button,
  Flex,
  forwardRef,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Slide,
  UseDisclosureProps,
} from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";
import { FilterTagButton } from "../../../../components/buttons/FilterTag";
import { FilterIcon } from "../../../../components/icons/FilterIcon";
import {
  getArtists,
  getComics,
  getReleases,
  getBundles,
} from "../../api/browseScreenApi";
import { BrowseFilters } from "./BrowseFilters";
import { isSafari } from "react-device-detect";
import { BrowseTabType } from "../../../../types";
import { Z_INDEX_MODAL } from "../../../root/constants";
import {
  FilterState,
  DEFAULT_FILTER_STATE,
  buildApiParams,
} from "../../utils/browseUrlParser";

interface BrowseFiltersModalProps extends UseDisclosureProps {
  filters: FilterState;
  onApply: (draft: FilterState) => void;
  type: BrowseTabType;
  small?: boolean;
  invalidate?: () => void;
}
export function BrowseFiltersModal({
  filters,
  onApply,
  isOpen,
  onOpen,
  onClose,
  type,
  small,
  invalidate,
}: BrowseFiltersModalProps) {
  return (
    <>
      {small ? (
        <SmallFilterModalTrigger onClick={onOpen} />
      ) : (
        <FilterTagButton
          alt={true}
          onClick={onOpen}
          isActive={isOpen}
          icon={<FilterIcon />}
          text="All Filters"
        />
      )}
      <FilterModal
        filters={filters}
        onApply={onApply}
        isOpen={isOpen}
        onClose={onClose}
        small={small}
        type={type}
        invalidate={invalidate}
      />
    </>
  );
}

export const SmallFilterModalTrigger = forwardRef((props, ref) => (
  <Button
    borderRadius="20px"
    background="charcoal.charcoal"
    backdropFilter="blur(18px)"
    border="1px solid"
    borderColor="transparent.white.10"
    boxShadow="0px 4px 24px 0px rgba(0, 0, 0, 0.40)"
    height="48px"
    justifyContent="center"
    alignItems="center"
    px="16px"
    py="14px"
    ref={ref}
    aria-label="All Filters Modal"
    {...props}
  >
    <FilterIcon width="20px" height="20px" color="dune.dune" />
  </Button>
));

interface FilterModalProps {
  filters: FilterState;
  onApply: (draft: FilterState) => void;
  isOpen: boolean | undefined;
  onClose: (() => void) | undefined;
  small?: boolean;
  type: BrowseTabType;
  invalidate?: () => void;
}
function FilterModal({
  filters,
  onApply,
  isOpen,
  onClose,
  small,
  type,
  invalidate,
}: FilterModalProps) {
  const [draft, setDraft] = useState<FilterState>(filters);

  // Reset draft to current filters when modal opens
  useEffect(() => {
    if (isOpen) {
      setDraft(filters);
    }
  }, [isOpen]);

  const handleDraftChange = useCallback(
    (updates: Partial<FilterState>) => {
      setDraft((prev) => ({ ...prev, ...updates }));
    },
    [],
  );

  const handleClearDraft = useCallback(() => {
    setDraft((prev) => ({
      ...DEFAULT_FILTER_STATE,
      query: prev.query,
      sortOrder: prev.sortOrder,
      sortDuration: prev.sortDuration,
    }));
  }, []);

  const handleApply = useCallback(() => {
    invalidate?.();
    onApply(draft);
    onClose?.();
  }, [draft, onApply, onClose, invalidate]);

  return (
    <Modal
      variant="filters"
      isOpen={!!isOpen}
      onClose={() => onClose?.()}
      isCentered={!small}
    >
      <ModalOverlay />
      {isOpen &&
        (small ? (
          <Slide
            in={isOpen}
            direction="bottom"
            style={{ width: "100%", height: "100%", zIndex: Z_INDEX_MODAL }}
          >
            <ModalContentContent
              small={!!small}
              close={onClose}
              draft={draft}
              onDraftChange={handleDraftChange}
              clearDraft={handleClearDraft}
              onApply={handleApply}
              type={type}
            />
          </Slide>
        ) : (
          <ModalContentContent
            small={!!small}
            close={onClose}
            draft={draft}
            onDraftChange={handleDraftChange}
            clearDraft={handleClearDraft}
            onApply={handleApply}
            type={type}
          />
        ))}
    </Modal>
  );
}

interface ModalContentContentProps {
  small: boolean;
  close?: () => void;
  draft: FilterState;
  onDraftChange: (updates: Partial<FilterState>) => void;
  clearDraft: () => void;
  onApply: () => void;
  type: BrowseTabType;
}
function ModalContentContent({
  small,
  close,
  draft,
  onDraftChange,
  clearDraft,
  onApply,
  type,
}: ModalContentContentProps) {
  return (
    <ModalContent
      containerProps={{
        alignItems: small ? "flex-end" : undefined,
      }}
      width={small ? "100vh" : undefined}
      margin={small ? 0 : undefined}
      minHeight={undefined}
      maxHeight={isSafari ? "90vh" : "100vh"}
      maxWidth="none"
      borderBottomRadius={small ? "none" : undefined}
      border="1px solid"
      borderColor="transparent.white.10"
    >
      <ModalHeader>
        <Flex
          width="100%"
          justifyContent="space-between"
          alignItems="center"
          flexDirection="row"
        >
          Filters
          <Button
            variant="ghostIcon"
            onClick={close}
            color="dune.600"
            _hover={{ color: "dune.100" }}
          >
            <CloseIcon w="16px" h="16px" />
          </Button>
        </Flex>
      </ModalHeader>
      <ModalBody className="styled-scrollbar">
        <Flex w="full">
          <BrowseFilters
            isModal
            tab={type}
            filters={draft}
            onFilterChange={onDraftChange}
          />
        </Flex>
      </ModalBody>
      <ModalFooter>
        <Button
          variant="ghostIcon"
          fontSize="xs"
          color="dune.600"
          _hover={{ color: "dune.100" }}
          fontWeight="500"
          lineHeight="150%"
          letterSpacing=".25px"
          onClick={clearDraft}
        >
          Clear all
        </Button>
        <ShowResultsButton draft={draft} type={type} onApply={onApply} />
      </ModalFooter>
    </ModalContent>
  );
}

interface ShowResultsButtonProps {
  draft: FilterState;
  type: BrowseTabType;
  onApply: () => void;
}
function ShowResultsButton({ draft, type, onApply }: ShowResultsButtonProps) {
  const apiParams = useMemo(() => buildApiParams(draft, type), [draft, type]);

  const filtersKey = useMemo(() => {
    const sorted = new URLSearchParams(apiParams);
    sorted.sort();
    return sorted.toString();
  }, [apiParams]);

  const { data: comicData } = useQuery({
    enabled: type === BrowseTabType.comics,
    queryKey: ["browseModalCount", filtersKey, type],
    queryFn: () => getComics(apiParams),
  });

  const { data: releaseData } = useQuery({
    enabled: type === BrowseTabType.releases,
    queryKey: ["browseModalCount", filtersKey, type],
    queryFn: () => getReleases(apiParams),
  });

  const { data: artistData } = useQuery({
    enabled: type === BrowseTabType.creators,
    queryKey: ["browseModalCount", filtersKey, type],
    queryFn: () => getArtists(apiParams),
  });

  const { data: bundleData } = useQuery({
    enabled: type === BrowseTabType.bundles,
    queryKey: ["browseModalCount", filtersKey, type],
    queryFn: () => getBundles(apiParams),
  });

  const data =
    type === BrowseTabType.comics
      ? comicData
      : type === BrowseTabType.releases
        ? releaseData
        : type === BrowseTabType.bundles
          ? bundleData
          : artistData;

  const totalResults = data?.data?.payload.pagination.total_results;
  return (
    <Button variant="primary" onClick={onApply}>
      Show {totalResults !== undefined ? totalResults : ""} Results
    </Button>
  );
}
